import { useState } from "react";
import { Star } from "lucide-react";
export { CallSlotPanel } from "@/components/admin/CallSlot";
import { toast } from "sonner";
import { api, apiError, imageUrl } from "@/lib/api";
import { netWorthLabel } from "@/components/apply/TrackFields";
import { cn } from "@/lib/utils";

export const ApplicationDetails = ({ a }) => {
  const app = a.application || {};
  const rows = app.track === "man"
    ? [["Net worth (self-declared)", netWorthLabel(app.net_worth_band, app.net_worth_other)], ["Occupation", app.occupation], ["Company", app.company], ["Reference", app.reference]]
    : [];
  return (
    <div className="space-y-4" data-testid={`application-details-${a.id}`}>
      {app.photos?.length > 0 && (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
          {app.photos.map((p, i) => (
            <a key={p} href={imageUrl(p)} target="_blank" rel="noreferrer" className="block aspect-[3/4] overflow-hidden rounded-md border border-border" data-testid={`application-photo-${a.id}-${i}`}>
              <img src={imageUrl(p)} alt={`${a.name} photo ${i + 1}`} className="img-cover" loading="lazy" />
            </a>
          ))}
        </div>
      )}
      {!app.photos?.length && <p className="text-sm text-muted-foreground" data-testid={`application-no-media-${a.id}`}>No photos added.</p>}
      {rows.length > 0 && (
        <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          {rows.filter(([, v]) => v).map(([k, v]) => <div key={k}><dt className="text-muted-foreground">{k}</dt><dd className="break-words">{v}</dd></div>)}
        </dl>
      )}
      <div className="flex flex-wrap gap-4 text-sm">
        {app.instagram && <a className="text-primary underline underline-offset-4" href={`https://instagram.com/${app.instagram}`} target="_blank" rel="noreferrer" data-testid={`application-instagram-${a.id}`}>Instagram @{app.instagram}</a>}
        {app.linkedin && <a className="text-primary underline underline-offset-4" href={app.linkedin} target="_blank" rel="noreferrer" data-testid={`application-linkedin-${a.id}`}>LinkedIn profile</a>}
      </div>
    </div>
  );
};

export const STAGES = { shortlisted: "Shortlisted", call_scheduled: "Call scheduled", called: "Called" };

export const ScreeningPanel = ({ a, onSaved }) => {
  const [rating, setRating] = useState(a.screening?.rating || null);
  const [stage, setStage] = useState(a.screening?.stage || null);
  const [notes, setNotes] = useState(a.screening?.notes || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/admin/applications/${a.id}/screening`, { rating, notes, stage }); toast.success("Screening saved"); onSaved?.(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-dashed border-primary/40 p-4" data-testid={`screening-${a.id}`}>
      <p className="text-xs uppercase tracking-[0.2em] text-primary">Private screening · admins only</p>
      <div className="mt-3 flex items-center gap-1" role="radiogroup" aria-label="Screening rating">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" role="radio" aria-checked={rating === n} aria-label={`${n} of 5`} onClick={() => setRating(rating === n ? null : n)}
            className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-secondary" data-testid={`screening-rating-${a.id}-${n}`}>
            <Star className={cn("h-5 w-5", rating && n <= rating ? "fill-primary text-primary" : "text-muted-foreground")} />
          </button>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-2" role="radiogroup" aria-label="Screening status">
        {[[null, "No status"], ...Object.entries(STAGES)].map(([id, label]) => (
          <button key={label} type="button" role="radio" aria-checked={stage === id} className="chip" data-active={stage === id}
            onClick={() => setStage(id)} data-testid={`screening-stage-${a.id}-${id || "none"}`}>{label}</button>
        ))}
      </div>
      <textarea rows={2} maxLength={2000} className="field mt-3 py-2" placeholder="Private notes (never shown to the applicant or members)" value={notes} onChange={(e) => setNotes(e.target.value)} aria-label="Private screening notes" data-testid={`screening-notes-${a.id}`} />
      <button type="button" className="btn-outline mt-3" disabled={busy} onClick={save} data-testid={`screening-save-${a.id}`}>Save screening</button>
    </div>
  );
};
